import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { authApi, usersApi } from '../services/api';
import { safeStorage } from '../utils/safeStorage';
import type { User, RegisterRequest, AuthContextType } from '../types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Carregar dados do localStorage ao iniciar
  useEffect(() => {
    const storedToken = safeStorage.getItem('token');
    const storedUser = safeStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      const parsed: User = JSON.parse(storedUser);
      setUser(parsed);
      Sentry.setUser({ id: parsed.externalId, username: parsed.name, email: parsed.email });
    }

    setIsLoading(false);
  }, []);

  /** Salva a sessão após login (email/senha ou Google) e retorna o usuário completo */
  const saveSession = async (response: { token: string; externalId: string; name: string; email: string; role: string; refreshToken?: string }): Promise<User> => {
    safeStorage.setItem('token', response.token);
    if (response.refreshToken) {
      safeStorage.setItem('refreshToken', response.refreshToken);
    }
    setToken(response.token);

    let userData: User;
    try {
      const userDetails = await usersApi.getDetails(response.externalId);
      safeStorage.setItem('user', JSON.stringify(userDetails));
      setUser(userDetails);
      userData = userDetails;
    } catch {
      userData = {
        externalId: response.externalId,
        name: response.name,
        email: response.email,
        cpf: '',
        phone: '',
        role: response.role as User['role'],
      };
      safeStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    }

    Sentry.setUser({ id: userData.externalId, username: userData.name, email: userData.email });
    return userData;
  };

  // Re-lança o erro de API preservando o `code` estável do backend em `err.code`
  // — a UI roteia por ele (ver lib/errorCodes.ts) sem casar texto da mensagem.
  const rethrowApiError = (error: any, fallbackMessage: string): never => {
    const message = error.response?.data?.message || fallbackMessage;
    const err = new Error(message) as Error & { code?: string };
    err.code = error.response?.data?.code;
    throw err;
  };

  const login = async (email: string, password: string): Promise<User> => {
    try {
      const response = await authApi.login({ email, password });
      return await saveSession(response);
    } catch (error: any) {
      return rethrowApiError(error, 'Erro ao fazer login');
    }
  };

  const googleLogin = async (idToken: string): Promise<User> => {
    try {
      const response = await authApi.googleLogin(idToken);
      return await saveSession(response);
    } catch (error: any) {
      return rethrowApiError(error, 'Erro ao fazer login com Google');
    }
  };

  const register = async (data: RegisterRequest): Promise<void> => {
    try {
      await authApi.register(data);
      // Após registro, o usuário precisa verificar o e-mail antes de fazer login
    } catch (error: any) {
      rethrowApiError(error, 'Erro ao cadastrar usuário');
    }
  };

  const completeRegistration = async (token: string, name: string, password: string): Promise<User> => {
    try {
      const response = await authApi.completeRegistration({ token, name, password });
      // Bug 2026-06-22: usuário abria o link de completar cadastro em browser
      // que já tinha outra conta logada → após finalizar, sessão da conta
      // antiga continuava ativa silenciosamente (mesmo com saveSession
      // sobrescrevendo storage/state). Risco de segurança: user achava que
      // estava como a conta nova mas acessava dados da antiga.
      // clearSession() antes garante reset absoluto (storage + state +
      // Sentry user) — o saveSession a seguir constrói a sessão nova
      // do zero, sem interferência da anterior.
      clearSession();
      return await saveSession(response);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Erro ao completar cadastro';
      throw new Error(message);
    }
  };

  const refreshUser = async () => {
    const storedUser = safeStorage.getItem('user');
    if (!storedUser) return;
    const parsed: User = JSON.parse(storedUser);
    try {
      const updated = await usersApi.getDetails(parsed.externalId);
      safeStorage.setItem('user', JSON.stringify(updated));
      setUser(updated);
    } catch {}
  };

  // Cleanup puramente local — sem chamada de API. Use quando o backend já
  // invalidou a sessão (delete account, 401 SESSION_INVALIDATED) e chamar
  // /auth/logout só geraria erro em cascata.
  const clearSession = () => {
    safeStorage.removeItem('token');
    safeStorage.removeItem('refreshToken');
    safeStorage.removeItem('user');
    setToken(null);
    setUser(null);
    Sentry.setUser(null);
  };

  const logout = async () => {
    const refreshToken = safeStorage.getItem('refreshToken');
    if (refreshToken) {
      try { await authApi.logout(refreshToken); } catch {}
    }
    clearSession();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token,
        isLoading,
        login,
        googleLogin,
        register,
        completeRegistration,
        logout,
        clearSession,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
